/**
 * @file        src/pages/flight/SearchAnalytics.jsx
 * @description Flight Search Analytics — demand intelligence over
 *              flight_search_logs. Trends, lanes, timing and device, for the
 *              same rows the Flight Search History screen lists.
 *
 *              CHART RULES THIS PAGE KEEPS (see the repo's dataviz guidance):
 *              • ONE series → ONE colour. A bar chart whose every bar is a
 *                different hue is a rainbow: the category is already named on the
 *                axis, so colour there carries no information and only costs
 *                colour-blind readers the ability to compare lengths.
 *              • The only multi-colour chart is the trend, which has two real
 *                series — and it carries a legend and direct end labels, so
 *                identity is never colour-alone.
 *              • Every value is printed next to its bar. These are counts a
 *                planner acts on, not a shape to admire.
 *              • The palette was validated, not eyeballed (lightness band,
 *                chroma floor, CVD separation, normal-vision floor, contrast).
 * @author      Kinetika Labs
 * @created     2026-09-22
 * @changelog
 *   - 2026-09-22 Claude — Initial.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import { get } from '../../api/client';

/**
 * Validated categorical palette. Derived from the console's own teal/violet/amber
 * family and then DARKENED until every check passed against the glass surface —
 * the brand values themselves (#2dd4bf, #7c8cff, #f59e0b) sit above the lightness
 * band and under 3:1 contrast, which is why they are not used verbatim here.
 * Slot 1 is the colour of every single-series chart on this page.
 */
const C = {
  teal: '#0e9b92',
  indigo: '#5b6ef5',
  amber: '#c2740a',
  magenta: '#c0348b',
  olive: '#5a8a1f',
};

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const DOW = ['', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const n = (v) => Number(v) || 0;
const fmt = (v) => n(v).toLocaleString('en-IN');

/** "2026-09" → "Sep 26". */
function monthLabel(key) {
  const [y, m] = String(key || '').split('-');
  return `${MONTHS[Number(m) - 1] || key} ${String(y || '').slice(2)}`;
}

/* ------------------------------------------------------------------ pieces -- */

function Kpi({ label, value, hint }) {
  return (
    <div className="an-kpi">
      <span className="an-kpi-label">{label}</span>
      <strong className="an-kpi-value">{value}</strong>
      {hint ? <span className="an-kpi-hint">{hint}</span> : null}
    </div>
  );
}

function Panel({ title, note, children, empty }) {
  return (
    <section className="an-panel">
      <header className="an-panel-head">
        <h3>{title}</h3>
        {note ? <span className="muted">{note}</span> : null}
      </header>
      {empty ? <p className="an-empty muted">{empty}</p> : children}
    </section>
  );
}

/**
 * Horizontal bars. ONE colour for the whole series — the label beside each bar is
 * what identifies it. Every bar carries its value, and the bar is a real element
 * (not an SVG path) so it stays legible at any zoom and in forced-colors mode.
 */
function Bars({ rows, max, unit = '' }) {
  const top = max || Math.max(1, ...rows.map((r) => n(r.value)));
  return (
    <ul className="an-bars">
      {rows.map((r) => (
        <li key={r.label} className="an-bar-row" title={`${r.label}: ${fmt(r.value)}${unit}`}>
          <span className="an-bar-label">{r.label}</span>
          <span className="an-bar-track">
            <span
              className="an-bar-fill"
              style={{ width: `${Math.max(2, (n(r.value) / top) * 100)}%`, background: C.teal }}
            />
          </span>
          <span className="an-bar-value">{fmt(r.value)}{unit}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Vertical columns for a time-of-day / month distribution. Same one-colour rule.
 * `barWidth` because a 7-bar chart across the same width as a 24-bar one would
 * otherwise draw seven slivers adrift in whitespace.
 */
function Columns({ rows, height = 130, barWidth = 26 }) {
  const top = Math.max(1, ...rows.map((r) => n(r.value)));
  return (
    <div className="an-cols" style={{ height, '--an-col-w': `${barWidth}px` }}>
      {rows.map((r) => (
        <div key={r.label} className="an-col" title={`${r.label}: ${fmt(r.value)}`}>
          <span className="an-col-bar" style={{ height: `${(n(r.value) / top) * 100}%`, background: C.teal }} />
          <span className="an-col-label">{r.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * The search trend. TWO series, so it carries a legend AND direct end labels —
 * identity is never colour-alone. One y-axis only: both series are counts of the
 * same thing, which is the only case where two lines may share a scale.
 */
function TrendChart({ series }) {
  const [hover, setHover] = useState(null);
  const pts = series.map((r) => ({
    day: String(r.day).slice(0, 10),
    total: n(r.searches),
    guests: n(r.guests),
  }));
  if (pts.length < 2) return <p className="an-empty muted">Not enough days in this range to plot a trend.</p>;

  const W = 640, H = 180, PAD_L = 8, PAD_R = 8, PAD_T = 12, PAD_B = 22;
  const top = Math.max(1, ...pts.map((p) => p.total));
  const x = (i) => PAD_L + (i * (W - PAD_L - PAD_R)) / (pts.length - 1);
  const y = (v) => PAD_T + (1 - v / top) * (H - PAD_T - PAD_B);
  const path = (key) => pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ');
  const area = `${path('total')} L${x(pts.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z`;
  const last = pts[pts.length - 1];

  return (
    <div className="an-trend">
      <div className="an-legend">
        <span><i style={{ background: C.teal }} /> All searches</span>
        <span><i style={{ background: C.indigo }} /> Guest (not signed in)</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="an-svg" role="img"
           aria-label={`Searches per day. Latest: ${last.total} searches, ${last.guests} from guests.`}>
        <defs>
          <linearGradient id="an-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.teal} stopOpacity="0.20" />
            <stop offset="100%" stopColor={C.teal} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Recessive gridlines — three is enough to read magnitude. */}
        {[0, 0.5, 1].map((f) => (
          <line key={f} x1={PAD_L} x2={W - PAD_R} y1={y(top * f)} y2={y(top * f)}
                stroke="rgba(20,35,90,.10)" strokeWidth="1" />
        ))}
        <path d={area} fill="url(#an-fill)" />
        <path d={path('total')} fill="none" stroke={C.teal} strokeWidth="2" strokeLinejoin="round" />
        <path d={path('guests')} fill="none" stroke={C.indigo} strokeWidth="2" strokeDasharray="4 3" strokeLinejoin="round" />
        {/* One hit target per day, wider than the mark, for the crosshair. */}
        {pts.map((p, i) => (
          <rect key={p.day} x={x(i) - (W / pts.length) / 2} y={0} width={W / pts.length} height={H}
                fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
        ))}
        {hover != null ? (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={PAD_T} y2={H - PAD_B} stroke="rgba(20,35,90,.25)" strokeWidth="1" />
            <circle cx={x(hover)} cy={y(pts[hover].total)} r="4" fill={C.teal} stroke="#fff" strokeWidth="2" />
            <circle cx={x(hover)} cy={y(pts[hover].guests)} r="4" fill={C.indigo} stroke="#fff" strokeWidth="2" />
          </g>
        ) : null}
      </svg>
      <div className="an-trend-foot">
        <span className="muted">{pts[0].day}</span>
        <span className="an-trend-read">
          {hover != null
            ? `${pts[hover].day} · ${fmt(pts[hover].total)} searches · ${fmt(pts[hover].guests)} guest`
            : `Latest ${last.day} · ${fmt(last.total)} searches`}
        </span>
        <span className="muted">{last.day}</span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- page -- */

export default function SearchAnalytics({
  title = 'Flight Search Analytics',
  subtitle = 'B2C Flight · what customers are looking for',
  audience = 'b2c',
  backTo = '/flight/search-history',
} = {}) {
  // The audience is fixed by the SCREEN and is NOT read from the query string
  // (CLAUDE.md §9d), so a shared link cannot cross the B2C/Corporate boundary.
  // Only the date range is URL-shareable.
  const [params] = useSearchParams();
  const [days, setDays] = useState(() => {
    const d = Number(params.get('days'));
    return RANGES.some((r) => r.days === d) ? d : 30;
  });

  const { data, isLoading } = useQuery({
    queryKey: ['flight-search-analytics', audience, days],
    queryFn: () => get('/flight/search-analytics', { params: { audience, days } }),
    placeholderData: keepPreviousData,
  });

  const a = data?.data;
  const s = a?.summary;
  const searches = n(s?.searches);
  const guestShare = searches ? Math.round((n(s?.guest_searches) / searches) * 100) : 0;

  const pct = (rows) => {
    const tot = rows.reduce((t, r) => t + n(r.searches), 0) || 1;
    return rows.map((r) => ({ label: r.label, value: Math.round((n(r.searches) / tot) * 100) }));
  };

  return (
    <>
      <PageHead title={title} subtitle={subtitle} />

      {/* Filters in ONE row above the charts. */}
      <div className="an-toolbar">
        <div className="seg" role="group" aria-label="Date range">
          {RANGES.map((r) => (
            <button key={r.days} type="button"
                    className={r.days === days ? 'active' : ''}
                    onClick={() => setDays(r.days)}>{r.label}</button>
          ))}
        </div>
        <Link className="btn ghost" to={backTo}>← Back to search history</Link>
      </div>

      {isLoading && !a ? <p className="muted">Loading analytics…</p> : null}

      {a ? (
        <>
          {!searches ? (
            <div className="notice-banner info">
              No flight searches in the last {days} days for this audience.
            </div>
          ) : null}

          <div className="an-kpis">
            <Kpi label="Searches" value={fmt(searches)} hint={`last ${days} days`} />
            <Kpi label="Signed-in customers" value={fmt(s.signed_in_users)}
                 hint={`${guestShare}% of searches were guests`} />
            <Kpi label="Routes searched" value={fmt(s.distinct_routes)} hint="distinct origin → destination" />
            <Kpi label="Avg booking window"
                 value={s.avg_lead_days == null ? '—' : `${Math.round(s.avg_lead_days)} days`}
                 hint="search date → departure" />
            <Kpi label="Avg party size"
                 value={s.avg_pax == null ? '—' : s.avg_pax.toFixed(1)} hint="passengers per search" />
          </div>

          <Panel title="Search volume" note="daily, with the share made by guests">
            <TrendChart series={a.series} />
          </Panel>

          <div className="an-grid">
            <Panel title="Top routes" note="origin → destination"
                   empty={!a.routes.length ? 'No routes in this range.' : null}>
              <Bars rows={a.routes.map((r) => ({ label: `${r.origin} → ${r.destination}`, value: r.searches }))} />
            </Panel>

            <Panel title="Where people fly from" note="top origins"
                   empty={!a.origins.length ? 'No origins in this range.' : null}>
              <Bars rows={a.origins.map((r) => ({ label: r.label, value: r.searches }))} />
            </Panel>

            <Panel title="Where people want to go" note="top destinations"
                   empty={!a.destinations.length ? 'No destinations in this range.' : null}>
              <Bars rows={a.destinations.map((r) => ({ label: r.label, value: r.searches }))} />
            </Panel>

            <Panel title="How far ahead they book" note="search date → departure"
                   empty={!a.leadBuckets.length ? 'No dated searches in this range.' : null}>
              <Bars rows={a.leadBuckets.map((r) => ({ label: r.label, value: r.searches }))} />
            </Panel>

            <Panel title="Trip type" note="share of searches">
              <Bars rows={pct(a.byType)} unit="%" max={100} />
            </Panel>

            <Panel title="Who is travelling" note="share of searches">
              <Bars rows={pct(a.byPax)} unit="%" max={100} />
            </Panel>

            <Panel
              title="Device"
              note={a.captured.device ? 'share of searches' : null}
              empty={!a.captured.device
                ? 'Not captured yet — run the booking migration (npm run migrate) to start recording device type.'
                : !a.byDevice.length ? 'No searches in this range.' : null}>
              <Bars rows={pct(a.byDevice)} unit="%" max={100} />
            </Panel>

            <Panel
              title="Browser"
              note={a.captured.browser ? 'share of searches' : null}
              empty={!a.captured.browser
                ? 'Not captured yet — run the booking migration to start recording the browser.'
                : !a.byBrowser.length ? 'No searches in this range.' : null}>
              <Bars rows={pct(a.byBrowser)} unit="%" max={100} />
            </Panel>

            <Panel
              title="Storefront"
              note={a.captured.brand ? 'which site the search came from' : null}
              empty={!a.captured.brand
                ? 'Not captured yet — run the booking migration to start recording the brand.'
                : !a.byBrand.length ? 'No searches in this range.' : null}>
              <Bars rows={a.byBrand.map((r) => ({ label: r.label, value: r.searches }))} />
            </Panel>
          </div>

          <Panel title="When people search" note="by hour of day, and by weekday">
            <Columns rows={Array.from({ length: 24 }, (_, h) => ({
              label: String(h).padStart(2, '0'),
              value: n(a.byHour.find((r) => Number(r.hour) === h)?.searches),
            }))} />
            <div style={{ height: 18 }} />
            <Columns rows={Array.from({ length: 7 }, (_, i) => ({
              label: DOW[i + 1],
              value: n(a.byDow.find((r) => Number(r.dow) === i + 1)?.searches),
            }))} height={96} barWidth={54} />
          </Panel>

          <Panel title="When people want to travel" note="departure month of the searched trips"
                 empty={!a.byMonth.length ? 'No dated searches in this range.' : null}>
            <Columns rows={a.byMonth.map((r) => ({ label: monthLabel(r.label), value: r.searches }))}
                     barWidth={46} />
          </Panel>
        </>
      ) : null}
    </>
  );
}
